import {
  AccountProvider,
  BookingQueryParams,
} from '@collinsonx/constants/enums';
import { decodeJWT, signJWT, verifyJWT } from '@collinsonx/jwt';
import { getItem, JSONParse, removeItem, setItem } from '@lib';
import {
  OriginType,
  resetLoginRedirectCount,
  sendWarnLog,
  serializeError,
  trackLoginRedirect,
  Unknown,
} from '@lib/logging';
import { BookingWarn } from 'config/datadog';
import {
  BOOKING_ID,
  ENTRY_URL,
  FASTTRACK_CONSENT_ACCEPTED,
  FT_ALWAYS_AVAILABLE,
  IS_LOCATION_PRODUCT,
  IS_USER_VALIDATED,
  JWT,
  LANGUAGE,
  LOG_ACTION_PATH,
  LOUNGE_CODE,
  PAYLOAD,
  PLATFORM,
  REFERRER,
  SESSION_ID,
  VERSION,
} from 'config/storage';
import router from 'next/router';
import { signOut } from 'supertokens-auth-react/recipe/session';
import { FastTrackConsentPayload } from 'types/booking';
import { PayloadType } from 'types/payload';
import { clientIsReady } from 'utils/errorHandling';
import { resolveLocaleCode } from 'utils/formatLanguageParam';

import {
  DEFAULT_LANGUAGE_CODE,
  DEFAULT_PLATFORM,
  DEFAULT_VERSION,
} from './constants';
import { isFastTrackEntitledUser, useFastTrack } from './features';
import {
  FAQ_LK,
  FAQ_PP,
  FASTTRACK_CONSENT_TERMS_URL,
} from './productDefaultConfig';

const {
  bookingId,
  jwt: jwtParam,
  ln,
  loungeCode: lcParam,
  platform: platParam,
  referrer: referParam,
  version,
} = BookingQueryParams;

export enum ROUTES {
  AMEND_BOOKING = '/amend-booking',
  CANCEL_BOOKING = '/cancel-booking',
  CANCELLED_BOOKING_CONFIRMATION = '/cancelled-booking-confirmation',
  CHECK_CODE = '/auth/check-code',
  CHECK_FT_LOCATIONS = '/check-ft-locations',
  CHECK_SLOTS = '/check-slots',
  CONFIRM_AMEND = '/confirm-amendment',
  CONFIRM_AMEND_REFUND = '/confirm-amendment-refund',
  CONFIRM_BOOKING = '/confirm-booking',
  CONFIRM_PAYMENT = '/confirm-payment',
  DECLINE_AMEND = '/decline-amend',
  FAILURE_AMEND = '/failure-amendment-refund',
  FAILURE_BOOKING = '/failure-booking',
  FASTTRACK_CONSENT = '/fasttrack-consent',
  HOME = '/',
  LOGIN = '/auth/login',
  SHOW_FT_ENTITLEMENT = '/show-ft-entitlement-message',
  MAINENANCE = '/maintenance',
  SIGNUP = '/auth/signup',
}

let signOutInFlight = false;

export const closeSessionAndSignOut = async (
  data?: Record<string, unknown>
) => {
  if (signOutInFlight) return;
  signOutInFlight = true;

  removeItem(SESSION_ID);
  removeItem(IS_USER_VALIDATED);
  removeItem(LOG_ACTION_PATH);

  if (!isRoute(ROUTES.LOGIN)) {
    trackLoginRedirect({ ...data, initiatedOnRoute: getRoute() });
    await redirectTo(ROUTES.LOGIN, undefined, { replace: true });
  }

  try {
    await signOut();
  } catch (err) {
    sendWarnLog(BookingWarn.SIGN_OUT_FAILED, {
      error:
        err instanceof Response
          ? `Response ${err.status} ${err.url}`
          : serializeError(err),
      origin: OriginType.External,
      status: err instanceof Response ? err.status : Unknown,
    });
  } finally {
    signOutInFlight = false;
  }
};

export const getFAQUrl = (provider?: AccountProvider) => {
  if (provider === AccountProvider.LK) return FAQ_LK;
  if (
    provider === AccountProvider.PP ||
    provider === AccountProvider.PP_PREFERRED
  )
    return FAQ_PP;

  // Deliberate default: unknown or undefined providers get the PP FAQ
  return FAQ_PP;
};

const shallowHistory: string[] = [];

function pushRouteToHistory() {
  if (shallowHistory[shallowHistory.length - 1] !== router.pathname) {
    shallowHistory.push(router.pathname);
  }
}

function popRouteFromHisotry(): null | string {
  if (shallowHistory.length <= 1) return null;
  shallowHistory.pop();
  return shallowHistory[shallowHistory.length - 1];
}

export const redirectTo = async (
  route: ROUTES | string,
  params?: { [key: string]: string | string[] | undefined },
  options?: { replace?: boolean }
) => {
  const url = {
    pathname: route,
    query: params,
  };

  if (options?.replace) {
    await router.replace(url);
    return;
  }

  await router.push(url);
};

export const isRoute = (page: ROUTES) => getRoute() === page;

export const onRouteChange = () => {
  pushRouteToHistory();
  router.events.on('routeChangeComplete', pushRouteToHistory);
  return () => router.events.off('routeChangeComplete', pushRouteToHistory);
};

export const getPayloadDefaultValues = () => {
  const payloadCache: PayloadType = JSONParse(getItem(PAYLOAD), {});

  return {
    language: payloadCache.language || DEFAULT_LANGUAGE_CODE,
    platform: payloadCache.platform || DEFAULT_PLATFORM,
    version:
      payloadCache.version ||
      process.env.NEXT_PUBLIC_VERSION ||
      DEFAULT_VERSION,
  };
};

export const getParamsFromQueryAndStorage = (stateParams: {
  bookingId?: string;
  language?: string;
  loungeCode?: string;
  platform?: string;
  referrer?: string;
  token?: string;
  version?: string;
}) => {
  const cachedJwtString = getItem(JWT) ?? '';
  const urlParams = new URLSearchParams(window.location.search);

  const tokenQuery = urlParams.get(jwtParam);
  const loungeQuery = urlParams.get(lcParam);
  const referrerQuery = urlParams.get(referParam);
  const platformQuery = urlParams.get(platParam);
  const languageQuery = urlParams.get(ln);
  const versionQuery = urlParams.get(version);
  const bookingIdQuery = urlParams.get(bookingId);

  const tokenParam = tokenQuery ?? getItem(JWT) ?? stateParams.token;
  const loungeParam =
    loungeQuery ?? getItem(LOUNGE_CODE) ?? stateParams.loungeCode;
  const referrerParam =
    referrerQuery ?? getItem(REFERRER) ?? stateParams.referrer;
  const platformParam =
    platformQuery ??
    getItem(PLATFORM) ??
    stateParams.platform ??
    DEFAULT_PLATFORM;
  const languageParam =
    languageQuery ?? stateParams.language ?? resolveLocaleCode();
  const versionParam =
    versionQuery ||
    getItem(VERSION) ||
    stateParams.version ||
    process.env.NEXT_PUBLIC_VERSION ||
    DEFAULT_VERSION;
  const bookingIdParam =
    bookingIdQuery ?? getItem(BOOKING_ID) ?? stateParams.bookingId;

  setItem(LANGUAGE, languageParam);
  setItem(VERSION, versionParam);
  setItem(PLATFORM, platformParam);

  if (loungeParam) setItem(LOUNGE_CODE, loungeParam);
  if (tokenParam) setItem(JWT, tokenParam);
  if (referrerParam) setItem(REFERRER, referrerParam);
  if (bookingIdParam) setItem(BOOKING_ID, bookingIdParam);

  return {
    bookingIdParam,
    cachedJwtString,
    languageParam,
    loungeParam,
    platformParam,
    referrerParam,
    tokenParam,
    versionParam,
  };
};

export const authenticateToContinueSession = async (
  data?: Record<string, unknown>
) => {
  if (!clientIsReady) return;

  setItem(ENTRY_URL, getRoute());
  await closeSessionAndSignOut(data);
};

export const continueSession = async () => {
  if (!clientIsReady) return;

  const entryUrl = getItem(ENTRY_URL);
  removeItem(ENTRY_URL);
  resetLoginRedirectCount();

  const loungeCode = getItem(LOUNGE_CODE);
  const isLocationProduct = loungeCode?.endsWith('FT');

  if (useFastTrack && isLocationProduct) {
    const jwtString = getItem(JWT);
    if (jwtString) {
      try {
        const decoded = decodeJWT(jwtString) as { sourceCode?: string };
        if (
          isFastTrackEntitledUser({
            loungeCode: loungeCode ?? undefined,
            sourceCode: decoded.sourceCode,
          })
        ) {
          await redirectTo(ROUTES.SHOW_FT_ENTITLEMENT);
          return;
        }
      } catch {
        // Fall through to normal flow
      }
    }

    if (!(await getFastTrackConsentFromStorage())) {
      await redirectTo(ROUTES.FASTTRACK_CONSENT);
      return;
    }
  }

  await redirectTo(entryUrl || ROUTES.HOME);
};

const getRoute = () => window.location.pathname || router.pathname;

const getIsLocationProduct = (): boolean => {
  if (!clientIsReady) return false;
  return JSONParse(getItem(IS_LOCATION_PRODUCT), false);
};

const shouldRedirectHomeFromConfirmBooking = (): boolean => {
  if (!isRoute(ROUTES.CONFIRM_BOOKING)) return false;

  try {
    const alwaysAvailable = JSONParse(getItem(FT_ALWAYS_AVAILABLE), false);
    const isLocation = getIsLocationProduct();
    return useFastTrack && isLocation && alwaysAvailable;
  } catch {
    return false;
  }
};

export const isPageProtected = () => {
  if (!clientIsReady) return false;

  const route = window.location.pathname || router.pathname;

  return (
    route !== ROUTES.SIGNUP &&
    route !== ROUTES.LOGIN &&
    route !== ROUTES.CHECK_CODE
  );
};

export const isMaintenancePage = () => {
  const route = getRoute();

  return route === ROUTES.MAINENANCE;
};

export const redirectToPreviousRoute = () => {
  if (shouldRedirectHomeFromConfirmBooking()) {
    redirectTo(ROUTES.HOME);
    return;
  }
  const previousRoute = popRouteFromHisotry();
  if (previousRoute) redirectTo(previousRoute);
};
export const reloadRoute = () => {
  router.replace(router.asPath);
};

export const openNewWindow = (url: string) => {
  if (typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const openFastTrackTermsAndConditions = () => {
  openNewWindow(FASTTRACK_CONSENT_TERMS_URL);
};

export const setFastTrackConsentToStorage = async (
  val: boolean
): Promise<void> => {
  try {
    const payload: FastTrackConsentPayload = {
      fastTrackConsent: val,
    };

    const secret =
      process.env.NEXT_PUBLIC_JWT_SECRET || 'fallback-fastconsent-secret-key';
    const encryptedVal = await signJWT(payload, secret, '1hr');

    setItem(FASTTRACK_CONSENT_ACCEPTED, encryptedVal);
  } catch (error) {
    setItem(FASTTRACK_CONSENT_ACCEPTED, JSON.stringify(val));
  }
};

export const getFastTrackConsentFromStorage = async (): Promise<boolean> => {
  const encryptedConsent = getItem(FASTTRACK_CONSENT_ACCEPTED);

  if (!encryptedConsent) return false;

  try {
    const secret =
      process.env.NEXT_PUBLIC_JWT_SECRET || 'fallback-fastconsent-secret-key';
    const verificationResult = await verifyJWT(encryptedConsent, secret);

    if (!verificationResult.isValid) {
      removeItem(FASTTRACK_CONSENT_ACCEPTED);
      return false;
    }

    const payload = decodeJWT(encryptedConsent) as FastTrackConsentPayload;

    if (payload.fastTrackConsent !== true) {
      removeItem(FASTTRACK_CONSENT_ACCEPTED);
      return false;
    }

    return true;
  } catch (error) {
    removeItem(FASTTRACK_CONSENT_ACCEPTED);
    return false;
  }
};
